import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import { FORMAT_DATE_BACK, formatDate } from 'ui-seeker/utils'
import { useContextSelector } from "use-context-selector"
import { AppCurrencyContext, AppMainHotelsContext, AppDatesContext, AppMarketContext } from "app/providers"
import { getOptionValues } from "./index"
import { useFilterSection } from './useFilterSection'
import { useCommonFiltersItems, useCommonFiltersParity, useCommonFiltersAlerts } from './index'
import { useValidatorSeeker } from 'app/@hooks'
import { object } from 'yup'
import CommonFiltersContext from '../context'

export default function useCommonFilters(schema = object({})) {

    const section = useFilterSection()
    const { state, setState } = useContext(CommonFiltersContext)
    const market = useContextSelector(AppMarketContext, ({state}) => state.market)
    const monthYear = useContextSelector(AppDatesContext, ({state}) => state.monthYear)
    const currency = useContextSelector(AppCurrencyContext, ({state}) => state.currency)
    const mainHotels = useContextSelector(AppMainHotelsContext, ({state}) => state.mainHotels)
    const updateMainHotels = useContextSelector(AppMainHotelsContext, ({updateState}) => updateState)

    const {
		hotels, dateRange, maxDate, customDateRange, boardID, rateID, providerID, sources, connectivities,
		roomID, adults, kids, occupancy, sites, sitesList, nights, priceType, open, devices, payType, siteName,
		hotelChain, siteLocation, allHotels, mappingType, noNights, customDate, minStay, nightsStatic, province,
		cityFrom, cityTo, cityFromName, cityToName, hotelName, rawRooms, location, hotelHistorical, year, otherSites,
		elementsOtherSites, devicesStatic, specificDays, calendarType, sitesListRealTime, hotelsRealTime, totalSites,
        realtimeMarket, defaultNights, defaultOccupancy, defaultOtas, idMainHotelCustomPinned, excludedHotels,

		setHotels, setDateRange, setCustomDateRange, setMaxDate, setBoard, setRate, setDevices, setPayType,
		setProviders, setRooms, setAdults, setKids, setOccupancy, setSites, setSitesList, setNights, setOpen,
		setPriceType, setSources, setConnectivities, setHotelChain,  setSiteLocation, setAllHotels,
		setMappingType, setDefaultOccupancy, setCustomDate, setMinStay, setNightsStatic, setTotalSites,
		setCityFrom, setCityTo, setRawRooms, setLocation, setHotelHistorical, setYear, setOtherSites, setProvince,
		setElementsOtherSites, setDevicesStatic, setDefaultNights, setSpecificDays, setCalendarType, setSitesListRealTime,
		setHotelsRealTime, setDefaultOtas, setRealtimeMarket, setIdMainHotelCustomPinned, setExcludedHotels
    } = useCommonFiltersItems()


	const { errorRegistry, isValid } = useValidatorSeeker({
		hotels, dateRange, maxDate, customDateRange, boardID, rateID, providerID, sources, connectivities,
		roomID, adults, kids, occupancy, sites, sitesList, nights, priceType, open, devices, payType, siteName,
		hotelChain, siteLocation, allHotels, mappingType, noNights, customDate, minStay, nightsStatic, province,
		cityFrom, cityTo, cityFromName, cityToName, hotelName, rawRooms, location, hotelHistorical, year, otherSites,
		elementsOtherSites, devicesStatic, specificDays, calendarType, sitesListRealTime, hotelsRealTime,
		defaultNights, defaultOccupancy, defaultOtas }, schema)

	const { 
		metas, otas, deviceParity, info,
		setMetas, setOtas, setDeviceParity, setInfo
	} = useCommonFiltersParity()

	const { 
		alerts, alertName, alertType, alertSubtype, isLoadingAlerts, comparison, ikey, maxRangeDate, alertsOccupancy,
		setAlerts, setAlertName, setAlertType, setAlertSubtype, setComparison, setIkeys, setAlertsOccupancy
	} = useCommonFiltersAlerts()

	const setMainHotels = (op) => {
		localStorage.setItem("selectedMainHotel", getOptionValues(op))
		updateMainHotels({ mainHotels: getOptionValues(op) })
	}

	const updateState = useCallback(newState => {
        setState(state => ({ ...state, ...newState}))
	}, [setState])

    const setLoading = useCallback(newState => {
        updateState({ isLoading: newState })
    }, [updateState])

	const setOpenFilters = useCallback(newState => {
        updateState({ openFilters: newState })
    }, [updateState])

    const useCustomDate = dateRange === 'custom'
	const useSelectOtas = sources === 'otas'

	const dataFilters = {
		dateRange: dateRange,
		dateSpecificList: specificDays,
		customDate: customDate,
		calendarType: calendarType,
		sitesListRealTime: sitesListRealTime,
		hotelsRealTime: hotelsRealTime,
		boardID: boardID,
		rateID: rateID,
		id_rate: rateID || priceType,
		startDate: (useCustomDate || calendarType === 'range') && customDateRange[0] ? formatDate(customDateRange[0], { format: FORMAT_DATE_BACK }) : null,
		endDate: (useCustomDate || calendarType === 'range') && customDateRange[1] ? formatDate(customDateRange[1], { format: FORMAT_DATE_BACK }) : null,
		hotelID: Number.isInteger(allHotels) ? allHotels : (!allHotels?.length ? hotels : allHotels),
		// hotels kept in the analyzer tables but left out of the compset (set from the table, mirrored here)
		excludedHotelID: excludedHotels || [],
		providerID: providerID,
		roomID: rawRooms,
		id_site: !!otherSites ? otherSites : (section === 'parity' ? sitesList : sites),
		siteID: section === 'parity' ? sitesList : sites,
		sitesList: Array.isArray(siteLocation) && !siteLocation.length ? sitesList : siteLocation,
		occupancy: (section === 'notifications' ? alertsOccupancy : occupancy),
		nights: nights,
		nightsStatic: nightsStatic,
		noNights,
		priceType: priceType,
		currency: currency || 'EUR',
		devices: devices,
		devicesStatic: devicesStatic,
		payType,
		metas,
		otas,
		sitePackageID: otas,
		routes: connectivities,
		deviceParity,
		sources,
		hotelChain,
		comparison,
		ikey,
		info,
		tp: isNaN(alertType) ? alertSubtype : alertType,
		idAlert: alertName,
		market,
		minStay: minStay,
		adults, kids, cityFrom, cityTo, siteName, cityFromName, cityToName, hotelName
	}

    const [loadingStates, setLoadingStates] = useState({
        connectivities: false, otas: false, hotels: false, nights: false, occupancy: false,
        rates: false, board: false, rawRooms:false, sites:false, alertName: false, province: false,
        location: false, hotelHistorical: false, year: false, dateRange: false, devices: false, cities: false, sitesList: false, comparison: false,
        ikeys: false, rate: false, priceType: false, rooms: false, markets: false, info: false, realtimeMarket: false
    });
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const handleLoadingChange = (filterName, isLoading) => {
        if (!mountedRef.current) return;
        setLoadingStates(prev => ({
            ...prev,
            [filterName]: isLoading
        }));
    };
    const isAnyFilterLoading = useMemo(() => {
        return Object.values(loadingStates).some(isLoading => isLoading);
    }, [loadingStates]);

    useEffect(() => {
        setLoading(isAnyFilterLoading);
    }, [isAnyFilterLoading, setLoading]);

	return {
		hotels, dateRange, maxDate, customDateRange, customDate, deviceParity, sources, mainHotels,
		boardID, rateID, providerID, roomID, adults, kids, occupancy, devices, payType, hotelChain, metas, siteLocation,
		sites, sitesList, nights, priceType, open, isLoading: state?.isLoading, otas, connectivities, allHotels, monthYear,
		alerts, maxRangeDate, alertName, alertType, alertSubtype, isLoadingAlerts, comparison, ikey, market,
		noNights, minStay, nightsStatic, siteName, cityFrom, cityTo, cityFromName, cityToName, hotelName,
		rawRooms, location, hotelHistorical, year, otherSites, elementsOtherSites, devicesStatic, info, province,
		specificDays, calendarType, sitesListRealTime, hotelsRealTime, alertsOccupancy, totalSites, currency, realtimeMarket,
		idMainHotelCustomPinned, excludedHotels,

		useCustomDate, useSelectOtas, mappingType,
		dataFilters, openFilters: state?.openFilters,
		dataFiltersString: JSON.stringify(dataFilters),
		errorRegistry, isValid,

        updateState, setLoading, setHotels, setDateRange, setCustomDateRange, setMaxDate, setBoard, setRate,
		setDevices, setPayType, setProviders, setRooms, setAdults, setKids, setOccupancy, setSites, setLocation,
		setSitesList, setNights, setSources, setOpen, setPriceType, setMetas, setOtas, setDeviceParity, setInfo,
		setConnectivities, setHotelChain, setMainHotels, setProvince, setTotalSites,
		setOpenFilters, setSiteLocation, setAllHotels, setMappingType, setAlerts, setAlertName, setAlertType,
		setAlertSubtype, setComparison, setIkeys, setRawRooms, setDefaultOccupancy, setCustomDate, setMinStay, setNightsStatic, setCityFrom,
		setCityTo, setHotelHistorical, setYear, setOtherSites, setElementsOtherSites, setDevicesStatic, setDefaultNights,
		setSpecificDays, setCalendarType, setSitesListRealTime, setHotelsRealTime, setDefaultOtas, setAlertsOccupancy,
		setRealtimeMarket, setIdMainHotelCustomPinned, setExcludedHotels,

        handleLoadingChange
    }
}